import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  checkSickSheetDates,
  eatDayOf,
  formatIsoDay,
  SICK_SHEET_DATE_MESSAGES,
  SICK_SHEET_RECOMMENDATION_LABELS,
  sickSheetDays,
  type SickSheetRecommendation,
} from '@wodi/domain';
import type { SickSheetView } from '@wodi/shared';
import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  DialogActions,
  RadioGroup,
  Select,
  TextArea,
} from '@wodi/ui';
import { useState } from 'react';

import { ApiError } from '../api/client.ts';
import { issueSickSheet } from '../api/clinical-documents.ts';
import { toasts } from '../toasts.ts';
import styles from './documents.module.css';
import { SickSheetPrinter } from './prints.tsx';

// Issuing a sick sheet (docs/plans/phase1-consultation.md §7.4; C19, TODO(clinical-verify)):
// rest from the visit's day or later (no backdating), no maximum yet, and the diagnosis
// left off unless the patient agrees. After issuing, the A5 preview opens for printing.

export interface DiagnosisChoice {
  id: string;
  words: string;
}

const REMARKS_MAX = 500;

function todayIso(): string {
  return formatIsoDay(eatDayOf(new Date()));
}

function daysWords(from: string | null, to: string | null): string | null {
  if (from === null || to === null || to < from) return null;
  const days = sickSheetDays(from, to);
  return days === 1 ? '1 day of rest' : `${String(days)} days of rest`;
}

/** The form; on success it shows the printer in the same dialog. */
function SickSheetForm({
  visitId,
  consultationId,
  visitStartedAt,
  diagnoses,
  listKeys,
  onIssued,
  onCancel,
}: {
  visitId: string;
  consultationId: string | null;
  visitStartedAt: string;
  diagnoses: readonly DiagnosisChoice[];
  listKeys: readonly QueryKey[];
  onIssued: (sheet: SickSheetView) => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const visitDay = formatIsoDay(eatDayOf(new Date(visitStartedAt)));
  const initial = todayIso() < visitDay ? visitDay : todayIso();
  const [clientRequestId] = useState(() => crypto.randomUUID());
  const [from, setFrom] = useState<string | null>(initial);
  const [to, setTo] = useState<string | null>(initial);
  const [recommendation, setRecommendation] = useState<SickSheetRecommendation>('off_duty');
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [diagnosisId, setDiagnosisId] = useState<string | null>(diagnoses[0]?.id ?? null);
  const [agreed, setAgreed] = useState(false);
  const [remarks, setRemarks] = useState('');
  const [tried, setTried] = useState(false);

  const dates = from === null || to === null ? null : checkSickSheetDates({ from, to, visitDay });
  const dateError =
    from === null || to === null
      ? 'Enter the first and last day of rest.'
      : dates !== null && !dates.ok
        ? SICK_SHEET_DATE_MESSAGES[dates.code]
        : null;
  const agreementError =
    showDiagnosis && !agreed
      ? 'The diagnosis is printed only when the patient agrees. Ask them, or leave it off.'
      : null;
  const remarksError =
    remarks.length > REMARKS_MAX ? `Keep remarks to ${String(REMARKS_MAX)} characters.` : null;

  const issue = useMutation({
    mutationFn: () =>
      issueSickSheet(visitId, {
        clientRequestId,
        consultationId,
        restFrom: from ?? '',
        restTo: to ?? '',
        recommendation,
        diagnosisId: showDiagnosis ? diagnosisId : null,
        patientAgreedToDiagnosis: showDiagnosis && agreed,
        remarks: remarks.trim() === '' ? null : remarks.trim(),
      }),
    onSuccess: (sheet) => {
      toasts.show({ title: 'Sick sheet issued' });
      for (const key of listKeys) void queryClient.invalidateQueries({ queryKey: key });
      onIssued(sheet);
    },
  });

  const serverError = issue.error instanceof ApiError ? issue.error.message : null;
  return (
    <>
      <div className={styles.fields}>
        <div className={styles.row}>
          <DateField
            label="First day of rest"
            value={from}
            onChange={setFrom}
            minValue={visitDay}
            isRequired
            {...(tried && dateError !== null ? { errorMessage: dateError } : {})}
          />
          <DateField
            label="Last day of rest"
            value={to}
            onChange={setTo}
            minValue={visitDay}
            isRequired
          />
        </div>
        <p className={styles.help} aria-live="polite">
          {daysWords(from, to) ?? 'Choose the dates to see the number of days.'}
        </p>
        <RadioGroup
          label="Recommendation"
          value={recommendation}
          onChange={(v) => {
            setRecommendation(v as SickSheetRecommendation);
          }}
          options={(['off_duty', 'light_duty'] as const).map((id) => ({
            id,
            label: SICK_SHEET_RECOMMENDATION_LABELS[id],
          }))}
        />
        <Checkbox
          isSelected={showDiagnosis}
          onChange={setShowDiagnosis}
          isDisabled={diagnoses.length === 0}
          description={
            diagnoses.length === 0
              ? 'No diagnosis is recorded in this visit.'
              : 'Off unless the patient agrees to the diagnosis being on the sheet.'
          }
        >
          Show diagnosis
        </Checkbox>
        {showDiagnosis ? (
          <>
            {diagnoses.length > 1 ? (
              <Select
                label="Diagnosis to show"
                options={diagnoses.map((d) => ({ id: d.id, label: d.words }))}
                value={diagnosisId}
                onChange={(key) => {
                  setDiagnosisId(key === null ? null : String(key));
                }}
              />
            ) : (
              <p className={styles.help}>{`Diagnosis: ${diagnoses[0]?.words ?? ''}`}</p>
            )}
            <Checkbox
              isSelected={agreed}
              onChange={setAgreed}
              {...(tried && agreementError !== null ? { errorMessage: agreementError } : {})}
            >
              Patient agreed
            </Checkbox>
          </>
        ) : null}
        <TextArea
          label="Remarks (optional)"
          description="Printed on the sheet."
          value={remarks}
          onChange={setRemarks}
          rows={2}
          {...(remarksError === null ? {} : { errorMessage: remarksError })}
        />
        {serverError === null ? null : (
          <p className={styles.help} role="alert">
            {serverError}
          </p>
        )}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={issue.isPending}
          pendingLabel="Issuing…"
          onPress={() => {
            setTried(true);
            if (dateError !== null || agreementError !== null || remarksError !== null) return;
            issue.mutate();
          }}
        >
          Issue sick sheet
        </Button>
      </DialogActions>
    </>
  );
}

/** "Issue sick sheet" and its dialog: the form, then the print preview. */
export function IssueSickSheet(props: {
  visitId: string;
  consultationId: string | null;
  visitStartedAt: string;
  diagnoses: readonly DiagnosisChoice[];
  listKeys: readonly QueryKey[];
  isDisabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState<SickSheetView | null>(null);
  const close = () => {
    setOpen(false);
    setIssued(null);
  };
  return (
    <>
      <Button
        isDisabled={props.isDisabled}
        onPress={() => {
          setOpen(true);
        }}
      >
        Issue sick sheet
      </Button>
      {open ? (
        <Dialog
          title={issued === null ? 'Issue sick sheet' : 'Print sick sheet'}
          isOpen
          onOpenChange={(next) => {
            if (!next) close();
          }}
        >
          {issued === null ? (
            <SickSheetForm {...props} onIssued={setIssued} onCancel={close} />
          ) : (
            <>
              <SickSheetPrinter id={issued.id} listKeys={props.listKeys} />
              <DialogActions>
                <Button onPress={close}>Done</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      ) : null}
    </>
  );
}
